import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import "./WelocateCompareModal.css";

const MAX_COMPARE = 3;

const formatPrice = (price) => {
  if (price == null) return null;
  return `€${price.toLocaleString("nl-NL")}`;
};

const getListingImage = (listing) => {
  return (listing?.files || [])
    .map((f) => f?.t_realmex_data_firehose_file_path)
    .find(Boolean);
};

const CompareItem = ({ listing, onRemove }) => {
  const image = getListingImage(listing);
  const title =
    listing?.title ||
    [listing?.postal, listing?.city].filter(Boolean).join(" ");
  const subtitle = [listing?.city, formatPrice(listing?.price)]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="wl-compare-item">
      <div className="wl-compare-avatar">
        {image && (
          <img src={image} alt={title} className="wl-compare-avatar-img" />
        )}
      </div>
      <div className="wl-compare-item-text">
        <div className="wl-compare-item-title">{title}</div>
        {subtitle && <div className="wl-compare-item-subtitle">{subtitle}</div>}
      </div>
      <button
        type="button"
        className="wl-compare-item-remove"
        onClick={onRemove}
        aria-label="Remove from compare"
      >
        <CloseIcon style={{ fontSize: "14px" }} />
      </button>
    </div>
  );
};

const WelocateCompareModal = ({
  compareList,
  onRemove,
  onClear,
  onClose,
  onOpenSideBySide,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  if (!compareList || compareList.length === 0) return null;

  const emptySlots = Math.max(0, MAX_COMPARE - compareList.length);
  const placeholders = Array.from({ length: emptySlots }, (_, i) => i);
  const canCompare = compareList.length >= 2;

  return (
    <div
      className={`wl-compare-modal ${
        isCollapsed ? "wl-compare-modal--collapsed" : ""
      }`}
      role="dialog"
      aria-label="Compare properties"
    >
      <div className="wl-compare-collapsed-bar">
        <span className="wl-compare-collapsed-label">
          Compare
          <span style={{ fontWeight: 800 }}> {compareList.length}</span>
          <span className="wl-compare-collapsed-label-max">/{MAX_COMPARE}</span>
        </span>
        <button
          type="button"
          className="wl-compare-collapsed-expand-btn"
          onClick={() => setIsCollapsed(false)}
          aria-label="Expand compare"
        >
          <KeyboardArrowDownIcon className="wl-compare-collapsed-expand-icon" />
        </button>
        <button
          type="button"
          className="wl-compare-collapsed-cta"
          onClick={onOpenSideBySide}
          disabled={!canCompare}
        >
          Compare
          <ArrowForwardIcon className="wl-compare-cta-icon" style={{width: "14px"}} />
        </button>
      </div>

      <div className="wl-compare-full">
        <div className="wl-compare-header">
          <div className="wl-compare-header-left">
            <CompareArrowsIcon className="wl-compare-header-icon" />
            <span className="wl-compare-header-title">COMPARE</span>
            <span className="wl-compare-counter">
              {compareList.length}/{MAX_COMPARE}
            </span>
          </div>
          <div className="wl-compare-header-right">
            <button
              type="button"
              className="wl-compare-clear-btn"
              onClick={() => {
                onClear && onClear();
                onClose && onClose();
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="wl-compare-collapse-btn"
              aria-label="Collapse compare"
              onClick={() => setIsCollapsed(true)}
            >
              <KeyboardArrowUpIcon />
            </button>
          </div>
        </div>

        <div className="wl-compare-list">
          {compareList.map((listing) => (
            <CompareItem
              key={listing.id ?? `${listing.lat}-${listing.lng}`}
              listing={listing}
              onRemove={() => onRemove(listing)}
            />
          ))}

          {placeholders.map((i) => (
            <div
              key={`empty-${i}`}
              className={`wl-compare-empty ${
                i === 0 ? "wl-compare-empty--next" : "wl-compare-empty--more"
              }`}
            >
              {i === 0 ? "View another property and click on compare to add" : "Add one more property to compare"}
            </div>
          ))}
        </div>

        {canCompare && (
          <button
            type="button"
            className="wl-compare-cta"
            onClick={onOpenSideBySide}
          >
            Compare side by side
            <ArrowForwardIcon className="wl-compare-cta-icon" />
          </button>
        )}
      </div>
    </div>
  );
};

export default WelocateCompareModal;
